import type { CustomerCardDto } from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useAdjustPoints, useCustomerLoyalty } from '../api/loyalty.js';
import { Can } from '../auth/session.js';
import { ErrorText } from '../components/ErrorText.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime, formatMoney } from '../format.js';
import { t } from '../i18n/uk.js';

const tl = t.loyalty;

/** A due date is the first instant of the next Kyiv day: show the last day the points live. */
const lastDay = (iso: string | null) =>
  iso ? formatDateTime(new Date(new Date(iso).getTime() - 1).toISOString()).slice(0, 10) : tl.never;

function AdjustForm({ customerId }: { customerId: string }) {
  const adjust = useAdjustPoints(customerId);
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const n = Number(points);
  const valid = points.trim() !== '' && Number.isInteger(n) && n !== 0 && reason.trim() !== '';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    adjust.mutate(
      { points: n, reason: reason.trim() },
      {
        onSuccess: () => {
          setPoints('');
          setReason('');
        },
      },
    );
  };
  return (
    <form className="inline-form" onSubmit={submit}>
      <h3>{tl.adjustTitle}</h3>
      <input
        aria-label={tl.adjustPoints}
        placeholder={tl.adjustPoints}
        inputMode="numeric"
        value={points}
        onChange={(e) => setPoints(e.target.value)}
      />
      <input
        aria-label={tl.adjustReason}
        placeholder={tl.adjustReason}
        className="grow"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <button type="submit" disabled={!valid || adjust.isPending}>
        {tl.adjust}
      </button>
      <ErrorText error={adjust.error} />
    </form>
  );
}

/** Q-points on the customer card (LOY-10): balances, batches by due date, history, corrections. */
export function CustomerLoyalty({ card }: { card: CustomerCardDto }) {
  const loyalty = useCustomerLoyalty(card.id);
  return (
    <div className="card">
      <h2>{tl.cardTitle}</h2>
      <QueryState query={loyalty}>
        {(s) => (
          <>
            <dl className="totals">
              <dt>{tl.spendable}</dt>
              <dd>
                <strong>
                  {tl.pointsWorth(s.spendable, formatMoney(s.spendable * s.pointValue))}
                </strong>
              </dd>
              {s.pending > 0 && (
                <>
                  <dt>{tl.pendingPoints}</dt>
                  <dd>{s.pending}</dd>
                </>
              )}
              {s.debt > 0 && (
                <>
                  <dt>{tl.debt}</dt>
                  <dd className="error-text">{s.debt}</dd>
                </>
              )}
            </dl>
            {s.nearestExpiry && (
              <p className="muted">
                {tl.nearest(s.nearestExpiry.points, lastDay(s.nearestExpiry.at))}
              </p>
            )}
            {s.lots.some((l) => l.remaining > 0) && (
              <>
                <h3>{tl.lotsTitle}</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{tl.lotSource}</th>
                      <th className="num">{tl.earned}</th>
                      <th className="num">{tl.left}</th>
                      <th>{tl.expires}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lots
                      .filter((l) => l.remaining > 0)
                      .map((l) => (
                        <tr key={l.entryId}>
                          <td>
                            {l.orderId ? (
                              <Link to={`/orders/${l.orderId}`}>{tl.lotFromOrder}</Link>
                            ) : (
                              tl.lotAdjust
                            )}
                          </td>
                          <td className="num">{l.points}</td>
                          <td className="num">{l.remaining}</td>
                          <td>{lastDay(l.expiresAt)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </>
            )}
            <h3>{tl.historyEntries}</h3>
            {s.entries.length === 0 ? (
              <p className="muted">{tl.noEntries}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{tl.when}</th>
                    <th>{tl.what}</th>
                    <th className="num">{tl.points}</th>
                    <th>{tl.order}</th>
                    <th>{t.audit.who}</th>
                    <th>{tl.reason}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.entries.map((e) => (
                    <tr key={e.id} className={e.status === 'cancelled' ? 'muted' : undefined}>
                      <td>{formatDateTime(e.createdAt)}</td>
                      <td>
                        {tl.kinds[e.kind]}
                        {e.status !== 'active' && ` (${tl.statuses[e.status]})`}
                      </td>
                      <td className="num">
                        {e.points > 0 ? '+' : ''}
                        {e.points}
                      </td>
                      <td>
                        {e.orderId && <Link to={`/orders/${e.orderId}`}>{tl.lotFromOrder}</Link>}
                      </td>
                      <td>{e.createdByName ?? t.audit.system}</td>
                      <td className="muted">{e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <Can permission="loyalty.adjust">
              <AdjustForm customerId={card.id} />
            </Can>
          </>
        )}
      </QueryState>
    </div>
  );
}
